import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Typography,
} from "@material-ui/core"
import OpenInNewIcon from "@material-ui/icons/OpenInNew"
import ReplayIcon from "@material-ui/icons/Replay"
import { useCallback, useEffect, useState } from "react"
import { FLUX_LOGO_URL } from "../../../IntegracaoFlux/assets"
import {
    buscarCodigosVerificacaoFlux,
    buscarDetalhesEntregaFlux,
    buscarSituacaoEnvioFlux,
    buscarStatusEntregaFlux,
    reenviarEntregaFlux,
} from "../services"

// Deixa a mensagem de erro da Flux mais legível: tira o prefixo "ERRO" e, quando o motivo é um
// JSON de erro HTTP, extrai o campo "error"/"message".
function formatarErro(mensagem) {
    if (!mensagem) return null
    let texto = String(mensagem).replace(/^ERRO(\s+INESPERADO)?\s*/i, "").trim()
    const ini = texto.indexOf("{")
    if (ini >= 0) {
        try {
            const obj = JSON.parse(texto.slice(ini))
            const partes = []
            if (texto.slice(0, ini).trim()) partes.push(texto.slice(0, ini).trim())
            if (obj.error) partes.push(obj.error)
            if (obj.message && obj.message !== obj.error) partes.push(obj.message)
            if (partes.length) return partes.join(" — ")
        } catch {
            /* mantém o texto cru */
        }
    }
    return texto
}

// Normaliza a resposta de /obter-codigos-verificacao. Formato da Flux:
// { codigoColeta, codigoRetorno, codigoConfirmacaoCliente, nomeEntregador, whatsappEntregador }.
// Tolera aninhamento em codigosEntrega/codigosVerificacao/data e alguns aliases.
function extrairFonte(payload) {
    if (!payload || typeof payload !== "object") return null
    return payload.codigosEntrega || payload.codigosVerificacao || payload.data || payload
}

function extrairCodigos(payload) {
    const fonte = extrairFonte(payload)
    if (!fonte) return null
    const codigos = {
        cliente: fonte.codigoConfirmacaoCliente ?? fonte.cliente ?? fonte.codigoCliente ?? null,
        coleta: fonte.codigoColeta ?? fonte.coleta ?? null,
        retorno: fonte.codigoRetorno ?? fonte.retorno ?? null,
    }
    if (codigos.cliente == null && codigos.coleta == null && codigos.retorno == null) return null
    return codigos
}

function extrairEntregador(payload) {
    const fonte = extrairFonte(payload)
    if (!fonte) return null
    const nome = fonte.nomeEntregador ?? null
    const telefone = fonte.whatsappEntregador ?? fonte.telefoneEntregador ?? null
    if (nome == null && telefone == null) return null
    return { nome, telefone }
}

// Fallback: extrai os códigos do texto salvo em observacao
// ("Códigos de entrega — cliente: 7488 | coleta: 7740 | retorno: 3449").
function extrairCodigosDaObservacao(obs) {
    if (!obs || !/c[óo]digos/i.test(obs)) return null
    const pega = re => {
        const m = obs.match(re)
        return m ? m[1].trim() : null
    }
    const codigos = {
        cliente: pega(/cliente:\s*([^\s|]+)/i),
        coleta: pega(/coleta:\s*([^\s|]+)/i),
        retorno: pega(/retorno:\s*([^\s|]+)/i),
    }
    if (codigos.cliente == null && codigos.coleta == null && codigos.retorno == null) return null
    return codigos
}

function Linha({ rotulo, valor }) {
    if (valor == null || valor === "") return null
    return (
        <Grid item xs={12} container justifyContent="space-between" style={{ gap: 8 }}>
            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                {rotulo}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 600, textAlign: "right" }}>
                {valor}
            </Typography>
        </Grid>
    )
}

// Cada código em sua própria linha, em destaque (fonte maior, monoespaçada).
function LinhaCodigo({ rotulo, valor }) {
    if (valor == null || valor === "") return null
    return (
        <Grid
            item
            xs={12}
            container
            justifyContent="space-between"
            alignItems="center"
            style={{ padding: "6px 0" }}
        >
            <Typography variant="body1" style={{ color: "#555" }}>
                {rotulo}
            </Typography>
            <Typography
                style={{
                    fontWeight: 700,
                    fontSize: 24,
                    letterSpacing: 2,
                    fontFamily: "monospace",
                }}
            >
                {valor}
            </Typography>
        </Grid>
    )
}

export default function DetalhesEntregaFlux({ aberto, onClose, pedido }) {
    const [carregando, setCarregando] = useState(false)
    const [detalhes, setDetalhes] = useState(null)
    const [statusFlux, setStatusFlux] = useState(null)
    const [codigos, setCodigos] = useState(null)
    const [entregadorFlux, setEntregadorFlux] = useState(null)
    const [erroEnvio, setErroEnvio] = useState(null)
    const [reenviando, setReenviando] = useState(false)
    const [sucessoReenvio, setSucessoReenvio] = useState(null)

    const carregar = useCallback(async () => {
        if (!pedido?.id) return
        setCarregando(true)
        try {
            const [resDetalhes, resStatus, resCodigos, resSituacao] = await Promise.all([
                buscarDetalhesEntregaFlux(pedido.id),
                buscarStatusEntregaFlux(pedido.id),
                buscarCodigosVerificacaoFlux(pedido.id),
                buscarSituacaoEnvioFlux(pedido.id),
            ])
            setDetalhes(resDetalhes?.data || null)
            const statusPayload = resStatus?.data
            const statusTexto =
                statusPayload?.status ||
                statusPayload?.rastreio ||
                statusPayload?.ultimoStatus ||
                null
            setStatusFlux(statusTexto)
            setCodigos(extrairCodigos(resCodigos?.data))
            setEntregadorFlux(extrairEntregador(resCodigos?.data))
            const situacao = resSituacao?.data
            setErroEnvio(
                situacao && situacao.existe && situacao.sucesso === false && situacao.mensagem
                    ? formatarErro(situacao.mensagem)
                    : null
            )
        } finally {
            setCarregando(false)
        }
    }, [pedido?.id])

    useEffect(() => {
        if (!aberto) return
        setSucessoReenvio(null)
        carregar()
    }, [aberto, carregar])

    const handleReenviar = async () => {
        if (!pedido?.id) return
        setReenviando(true)
        setSucessoReenvio(null)
        try {
            const res = await reenviarEntregaFlux(pedido.id)
            const situacao = res?.data || {}
            if (situacao.sucesso) {
                setErroEnvio(null)
                setSucessoReenvio("Pedido reenviado com sucesso para a Flux.")
                await carregar()
            } else {
                setErroEnvio(formatarErro(situacao.mensagem) || "Falha ao reenviar o pedido.")
            }
        } finally {
            setReenviando(false)
        }
    }

    const link = detalhes?.urlAcompanhamento
    const status = detalhes?.status || statusFlux
    const nomeEntregador = detalhes?.nomeEntregador || entregadorFlux?.nome
    const telefoneEntregador = detalhes?.telefoneEntregador || entregadorFlux?.telefone
    // Preferimos os códigos do endpoint ao vivo; se não vierem, extraímos da observação salva.
    const codigosFinais = codigos || extrairCodigosDaObservacao(detalhes?.observacao)

    return (
        <Dialog open={aberto} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Grid container alignItems="center" style={{ gap: 10 }}>
                    <img
                        src={FLUX_LOGO_URL}
                        alt="Flux"
                        style={{ height: 28, borderRadius: 4 }}
                    />
                    <span>Detalhes da entrega — Flux</span>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Grid container justifyContent="center" style={{ padding: 24 }}>
                        <CircularProgress size={28} />
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        {erroEnvio && (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        backgroundColor: "#fdecea",
                                        border: "1px solid #f5c6cb",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{ color: "#c62828", fontWeight: 700 }}
                                    >
                                        Falha ao enviar o pedido para a Flux
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#8a1c1c", wordBreak: "break-word" }}
                                    >
                                        {erroEnvio}
                                    </Typography>
                                    <Box mt={1.5}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disableElevation
                                            startIcon={
                                                reenviando ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    <ReplayIcon />
                                                )
                                            }
                                            disabled={reenviando}
                                            onClick={handleReenviar}
                                            style={{
                                                backgroundColor: reenviando ? "#e57373" : "#c62828",
                                                color: "white",
                                            }}
                                        >
                                            {reenviando ? "Reenviando..." : "Reenviar para a Flux"}
                                        </Button>
                                    </Box>
                                </div>
                            </Grid>
                        )}

                        {sucessoReenvio && (
                            <Grid item xs={12}>
                                <div
                                    style={{
                                        backgroundColor: "#e8f5e9",
                                        border: "1px solid #a5d6a7",
                                        borderRadius: 6,
                                        padding: "10px 12px",
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#2e7d32", fontWeight: 600 }}
                                    >
                                        {sucessoReenvio}
                                    </Typography>
                                </div>
                            </Grid>
                        )}

                        {status && (
                            <Grid item xs={12}>
                                <Chip
                                    label={status}
                                    size="small"
                                    style={{
                                        backgroundColor: "#1565c0",
                                        color: "white",
                                        fontWeight: 600,
                                    }}
                                />
                            </Grid>
                        )}

                        <Linha rotulo="Entregador" valor={nomeEntregador} />
                        <Linha rotulo="Telefone/WhatsApp" valor={telefoneEntregador} />
                        <Linha rotulo="Veículo" valor={detalhes?.veiculoEntregador} />
                        <Linha rotulo="Placa" valor={detalhes?.placaEntregador} />

                        {codigosFinais && (
                            <>
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                                        Códigos de verificação
                                    </Typography>
                                </Grid>
                                <LinhaCodigo rotulo="Cliente" valor={codigosFinais.cliente} />
                                <LinhaCodigo rotulo="Coleta" valor={codigosFinais.coleta} />
                                <LinhaCodigo rotulo="Retorno" valor={codigosFinais.retorno} />
                            </>
                        )}

                        {!erroEnvio && !status && !link && !codigosFinais && !nomeEntregador && (
                            <Grid item xs={12}>
                                <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                    Ainda não há detalhes de rastreio para esta entrega.
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                {link && (
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<OpenInNewIcon />}
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Acompanhar entrega
                    </Button>
                )}
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
